import React, { useEffect, useState } from 'react';
import { incidentsApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Loader2, Plus, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function Incidents() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', incident_type: 'incidencia', priority: 'media' });

  const load = () => { setLoading(true); incidentsApi.list().then(r => setItems(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const create = async () => {
    try { await incidentsApi.create(form); toast.success('Incidencia creada'); setOpen(false); setForm({ title: '', description: '', incident_type: 'incidencia', priority: 'media' }); load(); }
    catch { toast.error('Error'); }
  };
  const updateStatus = async (id, status) => {
    try { await incidentsApi.update(id, { status }); toast.success('Actualizada'); load(); }
    catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-4 animate-fade-in-up">
      <Card className="aea-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><AlertCircle className="w-5 h-5 text-aea-blue" /> Incidencias y consultas</CardTitle>
            <p className="text-sm text-slate-500 mt-1">Sistema de comunicación entre asociados y equipo AEA.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-aea-blue hover:bg-aea-blue-dk" data-testid="btn-new-incident"><Plus className="w-4 h-4 mr-2" /> Nueva</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nueva incidencia</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Título</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="inc-title" /></div>
                <div><Label>Descripción</Label><Textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="inc-desc" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Tipo</Label>
                    <Select value={form.incident_type} onValueChange={(v) => setForm({ ...form, incident_type: v })}>
                      <SelectTrigger data-testid="inc-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="incidencia">Incidencia</SelectItem>
                        <SelectItem value="reclamacion">Reclamación</SelectItem>
                        <SelectItem value="consulta">Consulta</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Prioridad</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger data-testid="inc-prio"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="baja">Baja</SelectItem>
                        <SelectItem value="media">Media</SelectItem>
                        <SelectItem value="alta">Alta</SelectItem>
                        <SelectItem value="critica">Crítica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button className="w-full bg-aea-blue hover:bg-aea-blue-dk" onClick={create} data-testid="btn-create-incident">Crear</Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-aea-blue" /></div> :
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3">Fecha</th>
                  <th className="text-left p-3">Título</th>
                  <th className="text-left p-3">Tipo</th>
                  <th className="text-left p-3">Prioridad</th>
                  <th className="text-left p-3">Estado</th>
                  <th className="text-right p-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Sin incidencias</td></tr>}
                {items.map(i => (
                  <tr key={i.id} className="border-t" data-testid={`inc-row-${i.id}`}>
                    <td className="p-3 font-mono text-xs">{new Date(i.created_at).toLocaleDateString('es-ES')}</td>
                    <td className="p-3"><div className="font-medium">{i.title}</div><div className="text-xs text-slate-500">{i.description?.slice(0, 60)}</div></td>
                    <td className="p-3 capitalize">{i.incident_type}</td>
                    <td className="p-3">
                      <span className={`status-badge ${i.priority === 'critica' ? 'status-invalid' : i.priority === 'alta' ? 'status-pending' : 'status-draft'}`}>{i.priority}</span>
                    </td>
                    <td className="p-3"><span className={`status-badge ${i.status === 'resuelta' || i.status === 'cerrada' ? 'status-effective' : 'status-pending'}`}>{i.status}</span></td>
                    <td className="p-3 text-right">
                      {isAdmin && i.status === 'abierta' && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(i.id, 'resuelta')} data-testid={`btn-resolve-${i.id}`}>Marcar resuelta</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </CardContent>
      </Card>
    </div>
  );
}
